import { BuilderFacilityInput } from "./Components/Facility";
import FilterSection from "./Components/FilterSection";
import { ReportParams } from "./Components/ReportParams";
import { Sidebar } from "./Components/Sidebar";
import { useQueryBuilder } from "./hooks/useQueryBuilder";
import QueryExecutor from "./QueryBuilder/QueryExecutor";
import { useSalesforce } from "./hooks/useSalesforce";
import { useEffect, useState } from "react";
import { IncidentQueryField } from "./types/salesforce";
import Modalant from "../../../../../components/ModalA/Modalant";
import EmailSubscriptionForm from "../../fall-analysis/form/EmailSubForm";
import { useStoreWrapper } from "../../../../../store/store";
import { toast } from "react-toastify";
import useSubscriptions from "../../../../../hooks/useSubscriptions";

const Builder = () => {
  const {
    query,
    orderBy,
    setOrderBy,
    groupBy,
    setGroupBy,
    setFilters,
    setLogicExpression,
    selectedFields,
    facilityId,
  } = useQueryBuilder();

  const { user } = useStoreWrapper((state) => ({
    user: state.user,
  }));

  const { getQueryFields, error } = useSalesforce();

  const [availableFields, setAvailableFields] = useState<IncidentQueryField[]>(
    []
  );

  const { handlePostSubscription } = useSubscriptions({});

  const { isLoading: isLoadingSubscription, mutateAsync: doPostSubscription } =
    handlePostSubscription;

  const [showSubscriptionModal, setShowSubscriptionModal] =
    useState<boolean>(false);

  useEffect(() => {
    const loadInitialFields = async () => {
      try {
        const fields = await getQueryFields();
        setAvailableFields(fields);
      } catch (err) {
        console.error("Failed to load initial fields:", err);
      }
    };
    loadInitialFields();
  }, [getQueryFields]);

  interface FormData {
    startDate: string;
    endDate: string;
    startTime: string;
    recurrence: string;
    description: string;
    weeklyDays?: string;
    monthlyDay?: string;
    periodType: string;
    periodLength: string;
  }

  const convertToString = (arr: any[]) => {
    return arr
      .map((item, index) => `level ${index + 1}: ${item.fieldName}`)
      .join(" ");
  };

  const handleFormSubmit = async (data: FormData) => {
    const subscription = {
      Report_Type__c: "Report Builder",
      Start_Date__c: data.startDate,
      End_Date__c: data.endDate,
      Interval_Type__c: data.recurrence,
      Description__c: data.description,
      User__c: user.userId,
      Facilities__c: facilityId.join(";"),
      ...(data.recurrence === "weekly" && {
        Day_Of_The_Week__c: data.weeklyDays,
      }),
      ...(data.recurrence === "monthly" && {
        Day_Of_The_Month__c: data.monthlyDay,
      }),
      Use_SOQL__c: true,
      SOQL_Query__c: query,
      Chart_Type__c: "Pie",
      Group_By__c: convertToString(groupBy),
      Trailing_Period__c: data.periodType,
      Trailing_Period_Quantity__c: data.periodLength,
    };

    try {
      const res = await doPostSubscription({ subscription });
      toast.success(res.data.message);
      setShowSubscriptionModal(false);
    } catch (err: any) {
      console.log({ err });
      toast.error(err.message);
    }
  };

  return (
    <div
      className="flex min-h-screen flex-col bg-gray-50"
      style={{ border: "1px solid lightgrey" }}
    >
      <header className="bg-white flex items-center justify-between border-b p-4">
        <h1 className="text-2xl font-bold">Report Builder</h1>
        <button
          onClick={() => {
            window.location.reload();
          }}
          className="cursor-pointer rounded border-0 bg-purple-400 p-1 text-white-main hover:bg-purple-500 md:w-auto"
        >
          Clear All
        </button>
      </header>

      <div className="flex flex-1 flex-col md:flex-row">
        <Sidebar className="bg-white w-full border-r" />

        <main className="flex-1 space-y-6">
          <div className="bg-white rounded-lg border p-4">
            <BuilderFacilityInput />
          </div>

          {/* <div className="grid grid-cols-4 gap-6"> */}
          <div className="flex flex-col border-black">
            {/* First Child: Smaller space */}
            <div className="bg-white col-span-2 ml-5 rounded-lg border p-6">
              <h2 className="mb-4 text-base text-gray-500">
                Report Parameters
              </h2>
              <ReportParams className="w-full" />
            </div>

            {/* Second Child: Larger space */}
            <div className="bg-white col-span-2 rounded-lg border border-gray-200 p-6">
              <h2 className="mb-4 text-base text-gray-500">Apply Filters</h2>
              <FilterSection
                availableFields={availableFields}
                selectedFields={selectedFields.map((f) => f.Field_API_Name__c)}
                orderBy={orderBy}
                onOrderByChange={setOrderBy}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                setFilters={setFilters}
                setLogicExpression={setLogicExpression}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <QueryExecutor
              onSubscribeClicked={() => {
                setShowSubscriptionModal(true);
              }}
              availableFields={availableFields}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-100 p-4 text-red-700">
              Error loading fields: {error}
            </div>
          )}
        </main>
      </div>
      {showSubscriptionModal && (
        <Modalant
          isLoading={isLoadingSubscription}
          className="items-center md:w-6/12"
          isOpen={showSubscriptionModal}
          isClose={() => setShowSubscriptionModal(false)}
        >
          <EmailSubscriptionForm
            handleSubmit={handleFormSubmit}
            handleClose={() => setShowSubscriptionModal(false)}
            isFacility={true}
            title={"Builder"}
            isLoading={isLoadingSubscription}
          />
        </Modalant>
      )}
    </div>
  );
};

export default Builder;
